import React, { useEffect } from 'react';
import { useCall } from '@/contexts/CallContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Phone, PhoneOff, Video } from 'lucide-react';

export function IncomingCallModal() {
  const { incomingCall, acceptCall, rejectCall } = useCall();

  // Trigger device vibration when incoming call is shown (mobile browsers)
  useEffect(() => {
    if (!incomingCall) return;

    let vibrationInterval: ReturnType<typeof setInterval> | null = null;
    if (navigator.vibrate) {
      try {
        // Vibration pattern: vibrate 500ms, pause 500ms, repeat
        const doVibrate = () => { try { navigator.vibrate([500, 500, 500, 500, 500]); } catch {} };
        doVibrate();
        vibrationInterval = setInterval(doVibrate, 3000);
      } catch {
        // Vibration blocked — no user gesture yet
      }
    }

    return () => {
      if (vibrationInterval) clearInterval(vibrationInterval);
      try { navigator.vibrate?.(0); } catch {}
    };
  }, [incomingCall]);

  if (!incomingCall) return null;

  const initials = incomingCall.callerName
    .split(' ')
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center gap-6 min-w-[320px] max-w-[400px] animate-in fade-in-0 zoom-in-95">
        {/* Pulsing ring animation */}
        <div className="relative">
          <div className="absolute inset-[-16px] rounded-full bg-green-400/20 animate-ping" style={{ animationDuration: '1.5s' }} />
          <div className="absolute inset-[-8px] rounded-full bg-green-400/15 animate-pulse" />
          <Avatar className="h-24 w-24 relative z-10 ring-4 ring-green-400/50 animate-bounce" style={{ animationDuration: '2s' }}>
            <AvatarImage src={incomingCall.callerAvatar || undefined} />
            <AvatarFallback className="text-2xl bg-gradient-to-br from-blue-500 to-purple-500 text-white">
              {initials}
            </AvatarFallback>
          </Avatar>
        </div>

        {/* Caller info */}
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">{incomingCall.callerName}</h2>
          <p className="text-sm text-gray-500 mt-1 flex items-center justify-center gap-1.5">
            {incomingCall.callType === 'video' ? (
              <><Video className="h-4 w-4" /> Incoming video call</>
            ) : (
              <><Phone className="h-4 w-4" /> Incoming audio call</>
            )}
          </p>
          {incomingCall.isGroupCall && (
            <p className="text-xs text-gray-400 mt-1">Group call</p>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-8">
          <div className="flex flex-col items-center gap-2">
            <Button
              variant="ghost"
              onClick={rejectCall}
              className="h-16 w-16 !rounded-full bg-red-600 hover:bg-red-700 text-white shadow-lg"
              size="icon"
            >
              <PhoneOff className="h-7 w-7" />
            </Button>
            <span className="text-xs text-gray-500">Decline</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Button
              variant="ghost"
              onClick={acceptCall}
              className="h-16 w-16 !rounded-full bg-green-600 hover:bg-green-700 text-white shadow-lg"
              size="icon"
            >
              <Phone className="h-7 w-7" />
            </Button>
            <span className="text-xs text-gray-500">Accept</span>
          </div>
        </div>
      </div>
    </div>
  );
}
